import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  useTheme,
} from "@mui/material";
import {
  PriorityHigh,
  LowPriority,
  Chat
} from "@mui/icons-material";
import { getPriorityConfig } from "../../utils/getPriorityConfig";

interface KanbanCardProps {
  id: string;
  task: any;
  onClick: (id: string) => void;
  isOverlay?: boolean;
}

export const KanbanCard: React.FC<KanbanCardProps> = ({
  id,
  task,
  onClick,
  isOverlay,
}) => {

  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ id });

  const isPlaceholder = isDragging && !isOverlay;
  const { color, icon, label } = getPriorityConfig(task.priority);

  const tags = Array.isArray(task.tags) ? task.tags.filter((value: string) => {
    return value.trim() !== "";
  }) : task.tags ? task.tags.split(",").filter((t: string) => t) : [];

  const visibleTags = tags.slice(0, 2);
  const remainingTags = tags.length - 2;
  const commentCount = task.comment_count || 0;

  return (
    <Card
      variant="outlined"
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onClick(id)}
      sx={{
        minHeight: 140,
        mb: 1.5,
        borderRadius: "12px",
        height: "auto",
        transition: "box-shadow 0.2s, border-color 0.2s",
        ...(isPlaceholder
          ? {
            border: "1px dashed",
            borderColor: theme.palette.divider,
            backgroundColor: "transparent",
            boxShadow: "none",
          }
          : {
            boxShadow: isOverlay ? theme.shadows[10] : theme.shadows[2],
            borderColor:
              theme.palette.mode === "dark"
                ? theme.palette.divider
                : "transparent",
            "&:hover": {
              boxShadow: theme.shadows[5],
            },
          }),
      }}
    >
      <Box sx={{ visibility: isPlaceholder ? "hidden" : "visible" }}>
        <CardContent>

          <Box sx={{ gap: 1.5, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Typography
                variant="subtitle1"
                fontWeight="500"
                sx={{
                  wordBreak: "break-word",
                  mr: 1,
                  lineHeight: 1.3,
                  fontSize: "0.95rem",
                }}
              >
                {task.task_name}
              </Typography>
              {label && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  sx={{ color: color, flexShrink: 0 }}
                >
                  {icon}
                  <Typography variant="caption" fontWeight="bold">
                    {label}
                  </Typography>
                </Box>
              )}
            </Box>

            {(
              tags.length > 0 ? (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {visibleTags.map((tag: string, index: number) => (
                    <Chip key={index} label={tag} size="small" sx={{ color: "text.secondary", borderRadius: 1 }} />
                  ))}
                  {remainingTags > 0 && (
                    <Chip label={`+${remainingTags}`} size="small" sx={{ color: "text.secondary", borderRadius: 1 }} />
                  )}
                </Box>
              ) :
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  No tags assigned
                </Typography>
            )}

            <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1.5}>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                {task.task_id}
              </Typography>
              <Box display="flex" alignItems="center" ml={"auto"} gap={0.5}>
                <Chat
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  {commentCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 25,
                  height: 25,
                  borderRadius: "50%",
                  bgcolor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "success.contrastText",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {task?.owner?.split(" ").filter(Boolean).map((word: string[]) => word[0].toUpperCase()).join("").slice(0, 2)}
              </Box>
            </Box>
          </Box>

        </CardContent>
      </Box>
    </Card>
  );
};
